import React from 'react';
import { Eye } from 'lucide-react';
import EnhancedTable from '../../components/common/EnhancedTable';
import type { Leaseholder } from '../../types/leaseholder';
import { formatTextWithLinks } from '../../utils/formatters';
import '../../components/common/EnhancedTable/EnhancedTableFix.css';

interface LeaseholderTableProps {
  leaseholders: Leaseholder[];
  onViewDetails: (leaseholder: Leaseholder) => void;
  sortField: string;
  sortDirection: 'asc' | 'desc';
  onSort: (field: string) => void;
}

export default function LeaseholderTable({
  leaseholders,
  onViewDetails,
  sortField,
  sortDirection,
  onSort
}: LeaseholderTableProps) {
  if (!leaseholders || leaseholders.length === 0) {
    return (
      <div className="p-6 text-center text-gray-500">
        No leaseholders found matching your criteria
      </div>
    );
  }

  const getSortIcon = (field: string) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  const handleSort = (field: string) => {
    onSort(field);
  };

  return (
    <EnhancedTable
      columns={[
        { 
          id: 'view', 
          header: 'View', 
          accessor: (row) => (
            <button
              onClick={() => onViewDetails(row)}
              className="text-primary hover:text-primary-dark"
            >
              <Eye className="h-5 w-5" />
            </button>
          ),
          width: 'narrow',
          align: 'center'
        },
        { 
          id: 'name', 
          header: (
            <div 
              className="cursor-pointer" 
              onClick={() => handleSort('name')}
            >
              Name {getSortIcon('name')}
            </div>
          ),
          accessor: (row) => (
            <div className="truncate" title={row.holder_name || '-'}>
              {formatTextWithLinks(row.holder_name || '-')}
            </div>
          ),
          width: 'medium'
        },
        { 
          id: 'lot_count', 
          header: (
            <div 
              className="cursor-pointer" 
              onClick={() => handleSort('lot_count')}
            >
              No. of Lots {getSortIcon('lot_count')}
            </div>
          ),
          accessor: (row) => row.lot_count || 0,
          width: 'narrow'
        },
        { 
          id: 'indicative_annual_value', 
          header: (
            <div 
              className="cursor-pointer" 
              onClick={() => handleSort('indicative_annual_value')}
            >
              Indicative Annual Value {getSortIcon('indicative_annual_value')}
            </div>
          ),
          accessor: (row) => row.indicative_annual_value 
            ? `$${row.indicative_annual_value.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}` 
            : '$0.00',
          width: 'medium',
          align: 'right'
        }
      ]}
      data={leaseholders}
      keyField="holder_id"
      width="1500px"
      maxHeight="calc(100vh - 16rem)"
      emptyMessage="No leaseholders found matching your criteria"
      horizontalScroll={true}
      className="enhanced-table-force-scroll"
    />
  );
}
